/** Client-side KLIPY search and selection dialog for information blocks. */

import { useCallback, useEffect, useState } from 'react';
import { IconSearch } from '@tabler/icons-react';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { fetchKlipyGifs, registerKlipyShare, type KlipyGif } from '../../lib/klipy';
import { KlipyAttribution } from '../klipy/KlipyAttribution';
import { Modal } from '../ui/Modal';
import { Skeleton } from '../ui/Skeleton';

export interface KlipyPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (gif: KlipyGif) => void;
}

/** Search KLIPY in provider order and persist one direct-hosted result. */
export function KlipyPicker({ open, onClose, onSelect }: KlipyPickerProps) {
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [gifs, setGifs] = useState<KlipyGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (search: string, signal?: AbortSignal) => {
    setLoading(true);
    setFailed(false);
    try {
      setGifs(await fetchKlipyGifs({ query: search, signal }));
    } catch (error) {
      if (signal?.aborted) return;
      captureException(error instanceof Error ? error : new Error(String(error)), 'KlipyPicker.load');
      setFailed(true);
      setGifs([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    void load(submittedQuery, controller.signal);
    return () => controller.abort();
  }, [load, open, submittedQuery]);

  return (
    <Modal open={open} onClose={onClose} title="Choose a GIF" maxWidth="max-w-3xl"
      heightClass="h-[min(720px,85vh)]" fullScreenOnMobile>
      <div className="flex h-full min-h-0 flex-col gap-4 max-sm:px-4 max-sm:pb-4">
        <form role="search" className="flex shrink-0 gap-2" onSubmit={(event) => {
          event.preventDefault();
          if (query.length <= 50) setSubmittedQuery(query);
        }}>
          <label className="relative flex-1">
            <span className="sr-only">Search GIFs</span>
            <IconSearch aria-hidden="true" size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="search" aria-label="Search GIFs" maxLength={50} value={query}
              onChange={(event) => setQuery(event.target.value)} placeholder="Search KLIPY"
              className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none transition focus:border-volt focus:ring-2 focus:ring-volt/20" />
          </label>
          <button type="submit" aria-label="Search"
            className="h-11 rounded-xl button-accent px-4 text-sm font-semibold transition">
            Search
          </button>
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {loading ? (
            <div aria-busy="true" className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: 12 }, (_, index) => <Skeleton key={index} height={130} borderRadius={10} />)}
            </div>
          ) : failed ? (
            <div className="flex h-full items-center justify-center">
              <button type="button" onClick={() => void load(submittedQuery)}
                className="rounded-xl border border-border px-4 py-2 text-sm text-foreground hover:bg-surface-hover">
                Try again
              </button>
            </div>
          ) : gifs.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No GIFs found</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {gifs.map((gif) => (
                <button key={gif.id} type="button" aria-label={gif.title} onClick={() => {
                  registerKlipyShare(gif);
                  onSelect(gif);
                  onClose();
                }} className="group relative h-32 overflow-hidden rounded-xl bg-black text-left outline-none ring-0 transition hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-volt">
                  <img src={gif.previewUrl} alt="" loading="lazy"
                    className="h-full w-full object-cover transition duration-200 group-hover:brightness-110" />
                </button>
              ))}
            </div>
          )}
        </div>

        <KlipyAttribution className="shrink-0 self-end" />
      </div>
    </Modal>
  );
}
